import { commitMutation, graphql, type Environment } from 'react-relay';

import { createAtlAttributionHeader } from '../../utils';
import type {
	emitFrontendCustomMetricMutation,
	ForgeObservabilityPublishFrontendCustomMetricInput,
} from './__generated__/emitFrontendCustomMetricMutation.graphql';

/**
 * Input for {@link emitFrontendCustomMetric}: the generated mutation input
 * (`ForgeObservabilityPublishFrontendCustomMetricInput`) plus the Relay environment used to send it.
 *
 * `contextAri` is the raw `contextIds[0]` (a workspace ARI), resolved backend-side; `moduleKey` is
 * the manifest module *key* (last ARI segment), not `extension.type`.
 */
export type EmitFrontendCustomMetricInput = ForgeObservabilityPublishFrontendCustomMetricInput & {
	relayEnvironment: Environment;
};

/**
 * Emits a Forge frontend custom metric.
 *
 * Sends the metric to AGG via the `ecosystem.forgeObservability.publishFrontendCustomMetric`
 * mutation (resolved by xen-logs-ingestor). Because the call is same-origin, the browser attaches
 * session cookies automatically; no token is passed. The call is fire-and-forget and silently fails
 * — emitting a metric must never surface an error to the app.
 *
 * AFM sends exactly 6 fields: { appId, installationId, contextAri, moduleKey, customMetricName,
 * value }. `environmentId` / `appVersion` are resolved backend-side via TCS lookups, so AFM never
 * sends them.
 */
export const emitFrontendCustomMetric = async (
	input: EmitFrontendCustomMetricInput,
): Promise<void> => {
	const { relayEnvironment, ...metricInput } = input;

	return new Promise<void>((resolve) => {
		// Resolve at most once; the metric is fire-and-forget so the response is ignored.
		let settled = false;
		const finish = () => {
			if (settled) {
				return;
			}
			settled = true;
			resolve();
		};

		commitMutation<emitFrontendCustomMetricMutation>(relayEnvironment, {
			mutation: graphql`
				mutation emitFrontendCustomMetricMutation(
					$input: ForgeObservabilityPublishFrontendCustomMetricInput!
				) {
					ecosystem {
						forgeObservability {
							publishFrontendCustomMetric(input: $input) {
								success
							}
						}
					}
				}
			`,
			variables: { input: metricInput },
			cacheConfig: {
				metadata: {
					headers: {
						'atl-attribution': createAtlAttributionHeader(),
					},
				},
			},
			// Fire-and-forget: the response is intentionally ignored.
			onCompleted: finish,
			// Silent fail: emitting a metric must never surface an error to the app.
			onError: finish,
		});
	});
};
